/* Pages de retour Stripe : succès + annulation.
 *
 * Le success page polle GET /api/payments/status/{session_id} pendant que le
 * webhook Stripe synchronise l'abonnement côté serveur.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Sparkles } from "lucide-react";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export function PaymentSuccessPage() {
  const query = useQuery();
  const navigate = useNavigate();
  const sessionId = query.get("session_id");
  const [status, setStatus] = useState("polling");
  const [details, setDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("no_session"); return; }
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
        if (attempts >= 12) { setStatus("timeout"); return; }
        setAttempts((a) => a + 1);
        timer = setTimeout(poll, 2500);
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="card-elevated max-w-lg w-full p-8 text-center" data-testid="payment-success-page">
        {status === "polling" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-accent" />
            <h1 className="mt-4 font-serif text-2xl">Confirmation du paiement…</h1>
            <p className="mt-2 text-muted-foreground text-sm">
              Nous synchronisons votre abonnement — cela prend quelques secondes.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-600" />
            <h1 className="mt-4 font-serif text-3xl">Bienvenue sur Fonbex Pro 🎉</h1>
            <p className="mt-3 text-muted-foreground text-sm">
              Votre abonnement est actif. Un e-mail de confirmation vient d&apos;être
              envoyé à l&apos;adresse liée à votre compte.
            </p>
            {details?.beta_discount_applied && (
              <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-accent/10 text-accent px-3 py-1 text-xs font-bold uppercase tracking-widest">
                <Sparkles className="h-3 w-3" /> Remise bêta -50 % à vie
              </div>
            )}
            <Button className="btn-pill mt-6 w-full" data-testid="payment-success-continue"
                    onClick={() => navigate("/app/assistant")}>
              Ouvrir Mon Assistant
            </Button>
          </>
        )}
        {(status === "failed" || status === "error" || status === "timeout" || status === "no_session") && (
          <>
            <XCircle className="h-10 w-10 mx-auto text-red-600" />
            <h1 className="mt-4 font-serif text-2xl">Confirmation impossible</h1>
            <p className="mt-3 text-muted-foreground text-sm">
              {status === "no_session"
                ? "Aucune session de paiement n'a été détectée."
                : status === "timeout"
                  ? "Le paiement met plus de temps que prévu à se confirmer. Il apparaîtra dans vos paramètres dès qu'il sera validé."
                  : "Nous n'avons pas pu confirmer votre paiement. Merci de nous contacter si votre carte a été débitée."}
            </p>
            <Button variant="outline" asChild className="mt-6">
              <Link to="/app/settings">Ouvrir mes paramètres</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancelPage() {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="card-elevated max-w-lg w-full p-8 text-center" data-testid="payment-cancel-page">
        <XCircle className="h-10 w-10 mx-auto text-muted-foreground" />
        <h1 className="mt-4 font-serif text-2xl">Paiement annulé</h1>
        <p className="mt-3 text-muted-foreground text-sm">
          Vous avez interrompu le processus — aucun débit n&apos;a été effectué.
          Reprenez quand vous êtes prêt.
        </p>
        <Button asChild className="btn-pill mt-6 w-full">
          <Link to="/app/subscription">Revoir les formules</Link>
        </Button>
      </div>
    </div>
  );
}
